import { computed, ref, unref, watch, type Ref } from 'vue';
import { defineStore } from '../store';
import type { KeySource, FetcherFn, DataResult, DataState, KeyType } from './types';
import { serializeKey } from './utils';

/**
 * Creates a fetcher with a cache mechanism to avoid refetching the same data.
 *
 * @param key The key to use to cache the data.
 * @param fetcher
 * @returns
 */
export function useFetchData<Key extends KeySource, Data>(
  key: KeyType<Key>,
  fetcher: FetcherFn<Key, Data>,
): DataResult<Data> {
  return useFetchDataStore().get(key, fetcher);
}

export const useFetchDataStore = defineStore('pi-fetchData', () => {
  const cache = new Map<string, Ref<DataState<any>>>();

  function get<Key extends KeySource, Data>(key: KeyType<Key>, fetcher: FetcherFn<Key, Data>): DataResult<Data> {
    const computedKey = typeof key === 'function' ? computed(key) : key;

    const skey = computed(() => serializeKey(unref<Key | undefined>(computedKey)));

    watch(skey, (k) => _refetch(k), { immediate: true });

    function _refetch(k: string | undefined, force = false) {
      if (!k) return;
      let result = cache.get(k);

      const isNew = !result;

      if (!result) {
        result = ref<DataState<Data>>({ loading: true });
        cache.set(k, result);
      }

      if (force || isNew) {
        if (result.value.data) result.value = { ...result.value };
        const promise = fetcher(unref<Key | undefined>(computedKey)!);
        promise.then(
          (data) => {
            result!.value = { data, loading: false };
          },
          (error) => {
            result!.value = { error };
          },
        );
      }
    }

    return {
      state: computed(() => {
        let item: Ref<DataState<any>> | undefined;
        if (skey.value) item = cache.get(skey.value);

        return item?.value ?? { loading: true, fetching: true };
      }),
      refetch: () => _refetch(skey.value, true),
    };
  }

  return { get };
});
